/**
 * Languages supported by Cloudflare Workers AI's m2m100 model.
 *
 * `country` is an ISO 3166-1 alpha-2 code (lowercase) that maps to a flag
 * in the `flag-icons` package. A few entries use the `gb-eng` / `gb-sct`
 * / `gb-wls` subdivision codes that flag-icons also ships with.
 */

export interface LanguageOption {
  code: string;
  english: string;
  native: string;
  /** ISO 3166-1 alpha-2 code (lowercase), or a flag-icons subdivision code. */
  country: string;
  /** If set, override the code sent to the translation backend. */
  translationCode?: string;
}

export const LANGUAGES: LanguageOption[] = [
  { code: 'af', english: 'Afrikaans', native: 'Afrikaans', country: 'za' },
  { code: 'am', english: 'Amharic', native: 'አማርኛ', country: 'et' },
  { code: 'ar', english: 'Arabic', native: 'العربية', country: 'sa' },
  { code: 'ary', english: 'Darija', native: 'الدارجة', country: 'ma', translationCode: 'ar' },
  { code: 'ast', english: 'Asturian', native: 'Asturianu', country: 'es' },
  { code: 'az', english: 'Azerbaijani', native: 'Azərbaycanca', country: 'az' },
  { code: 'ba', english: 'Bashkir', native: 'Башҡортса', country: 'ru' },
  { code: 'be', english: 'Belarusian', native: 'Беларуская', country: 'by' },
  { code: 'bg', english: 'Bulgarian', native: 'Български', country: 'bg' },
  { code: 'bn', english: 'Bengali', native: 'বাংলা', country: 'bd' },
  { code: 'br', english: 'Breton', native: 'Brezhoneg', country: 'fr' },
  { code: 'bs', english: 'Bosnian', native: 'Bosanski', country: 'ba' },
  { code: 'ca', english: 'Catalan', native: 'Català', country: 'es' },
  { code: 'ceb', english: 'Cebuano', native: 'Cebuano', country: 'ph' },
  { code: 'cs', english: 'Czech', native: 'Čeština', country: 'cz' },
  { code: 'cy', english: 'Welsh', native: 'Cymraeg', country: 'gb-wls' },
  { code: 'da', english: 'Danish', native: 'Dansk', country: 'dk' },
  { code: 'de', english: 'German', native: 'Deutsch', country: 'de' },
  { code: 'el', english: 'Greek', native: 'Ελληνικά', country: 'gr' },
  { code: 'en', english: 'English', native: 'English', country: 'gb' },
  { code: 'es', english: 'Spanish', native: 'Español', country: 'es' },
  { code: 'et', english: 'Estonian', native: 'Eesti', country: 'ee' },
  { code: 'fa', english: 'Persian', native: 'فارسی', country: 'ir' },
  { code: 'ff', english: 'Fulah', native: 'Fulfulde', country: 'sn' },
  { code: 'fi', english: 'Finnish', native: 'Suomi', country: 'fi' },
  { code: 'fr', english: 'French', native: 'Français', country: 'fr' },
  { code: 'fy', english: 'Western Frisian', native: 'Frysk', country: 'nl' },
  { code: 'ga', english: 'Irish', native: 'Gaeilge', country: 'ie' },
  { code: 'gd', english: 'Scottish Gaelic', native: 'Gàidhlig', country: 'gb-sct' },
  { code: 'gl', english: 'Galician', native: 'Galego', country: 'es' },
  { code: 'gu', english: 'Gujarati', native: 'ગુજરાતી', country: 'in' },
  { code: 'ha', english: 'Hausa', native: 'Hausa', country: 'ng' },
  { code: 'he', english: 'Hebrew', native: 'עברית', country: 'il' },
  { code: 'hi', english: 'Hindi', native: 'हिन्दी', country: 'in' },
  { code: 'hr', english: 'Croatian', native: 'Hrvatski', country: 'hr' },
  { code: 'ht', english: 'Haitian Creole', native: 'Kreyòl Ayisyen', country: 'ht' },
  { code: 'hu', english: 'Hungarian', native: 'Magyar', country: 'hu' },
  { code: 'hy', english: 'Armenian', native: 'Հայերեն', country: 'am' },
  { code: 'id', english: 'Indonesian', native: 'Bahasa Indonesia', country: 'id' },
  { code: 'ig', english: 'Igbo', native: 'Igbo', country: 'ng' },
  { code: 'ilo', english: 'Iloko', native: 'Ilokano', country: 'ph' },
  { code: 'is', english: 'Icelandic', native: 'Íslenska', country: 'is' },
  { code: 'it', english: 'Italian', native: 'Italiano', country: 'it' },
  { code: 'ja', english: 'Japanese', native: '日本語', country: 'jp' },
  { code: 'jv', english: 'Javanese', native: 'Basa Jawa', country: 'id' },
  { code: 'ka', english: 'Georgian', native: 'ქართული', country: 'ge' },
  { code: 'kk', english: 'Kazakh', native: 'Қазақ тілі', country: 'kz' },
  { code: 'km', english: 'Khmer', native: 'ខ្មែរ', country: 'kh' },
  { code: 'kn', english: 'Kannada', native: 'ಕನ್ನಡ', country: 'in' },
  { code: 'ko', english: 'Korean', native: '한국어', country: 'kr' },
  { code: 'lb', english: 'Luxembourgish', native: 'Lëtzebuergesch', country: 'lu' },
  { code: 'lg', english: 'Ganda', native: 'Luganda', country: 'ug' },
  { code: 'ln', english: 'Lingala', native: 'Lingála', country: 'cd' },
  { code: 'lo', english: 'Lao', native: 'ລາວ', country: 'la' },
  { code: 'lt', english: 'Lithuanian', native: 'Lietuvių', country: 'lt' },
  { code: 'lv', english: 'Latvian', native: 'Latviešu', country: 'lv' },
  { code: 'mg', english: 'Malagasy', native: 'Malagasy', country: 'mg' },
  { code: 'mk', english: 'Macedonian', native: 'Македонски', country: 'mk' },
  { code: 'ml', english: 'Malayalam', native: 'മലയാളം', country: 'in' },
  { code: 'mn', english: 'Mongolian', native: 'Монгол', country: 'mn' },
  { code: 'mr', english: 'Marathi', native: 'मराठी', country: 'in' },
  { code: 'ms', english: 'Malay', native: 'Bahasa Melayu', country: 'my' },
  { code: 'my', english: 'Burmese', native: 'မြန်မာ', country: 'mm' },
  { code: 'ne', english: 'Nepali', native: 'नेपाली', country: 'np' },
  { code: 'nl', english: 'Dutch', native: 'Nederlands', country: 'nl' },
  { code: 'no', english: 'Norwegian', native: 'Norsk', country: 'no' },
  { code: 'ns', english: 'Northern Sotho', native: 'Sesotho sa Leboa', country: 'za' },
  { code: 'oc', english: 'Occitan', native: 'Occitan', country: 'fr' },
  { code: 'or', english: 'Odia', native: 'ଓଡ଼ିଆ', country: 'in' },
  { code: 'pa', english: 'Punjabi', native: 'ਪੰਜਾਬੀ', country: 'in' },
  { code: 'pl', english: 'Polish', native: 'Polski', country: 'pl' },
  { code: 'ps', english: 'Pashto', native: 'پښتو', country: 'af' },
  { code: 'pt', english: 'Portuguese', native: 'Português', country: 'pt' },
  { code: 'ro', english: 'Romanian', native: 'Română', country: 'ro' },
  { code: 'ru', english: 'Russian', native: 'Русский', country: 'ru' },
  { code: 'sd', english: 'Sindhi', native: 'سنڌي', country: 'pk' },
  { code: 'si', english: 'Sinhala', native: 'සිංහල', country: 'lk' },
  { code: 'sk', english: 'Slovak', native: 'Slovenčina', country: 'sk' },
  { code: 'sl', english: 'Slovenian', native: 'Slovenščina', country: 'si' },
  { code: 'so', english: 'Somali', native: 'Soomaali', country: 'so' },
  { code: 'sq', english: 'Albanian', native: 'Shqip', country: 'al' },
  { code: 'sr', english: 'Serbian', native: 'Српски', country: 'rs' },
  { code: 'ss', english: 'Swati', native: 'siSwati', country: 'sz' },
  { code: 'su', english: 'Sundanese', native: 'Basa Sunda', country: 'id' },
  { code: 'sv', english: 'Swedish', native: 'Svenska', country: 'se' },
  { code: 'sw', english: 'Swahili', native: 'Kiswahili', country: 'tz' },
  { code: 'ta', english: 'Tamil', native: 'தமிழ்', country: 'in' },
  { code: 'th', english: 'Thai', native: 'ไทย', country: 'th' },
  { code: 'tl', english: 'Tagalog', native: 'Tagalog', country: 'ph' },
  { code: 'tn', english: 'Tswana', native: 'Setswana', country: 'bw' },
  { code: 'tr', english: 'Turkish', native: 'Türkçe', country: 'tr' },
  { code: 'uk', english: 'Ukrainian', native: 'Українська', country: 'ua' },
  { code: 'ur', english: 'Urdu', native: 'اردو', country: 'pk' },
  { code: 'uz', english: 'Uzbek', native: 'Oʻzbekcha', country: 'uz' },
  { code: 'vi', english: 'Vietnamese', native: 'Tiếng Việt', country: 'vn' },
  { code: 'wo', english: 'Wolof', native: 'Wolof', country: 'sn' },
  { code: 'xh', english: 'Xhosa', native: 'isiXhosa', country: 'za' },
  { code: 'yi', english: 'Yiddish', native: 'ייִדיש', country: 'il' },
  { code: 'yo', english: 'Yoruba', native: 'Yorùbá', country: 'ng' },
  { code: 'zh', english: 'Chinese', native: '中文', country: 'cn' },
  { code: 'zu', english: 'Zulu', native: 'isiZulu', country: 'za' },
];

const SUPPORTED = new Set(LANGUAGES.map((l) => l.code));

export function isSupportedLanguage(code: string): boolean {
  return SUPPORTED.has(code);
}

export function getLanguage(code: string): LanguageOption | undefined {
  return LANGUAGES.find((l) => l.code === code);
}

export function getTranslationCode(code: string): string {
  return getLanguage(code)?.translationCode ?? code;
}
